import { useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { FormModuleErrorCode, type FormModuleDetail } from "shared"
import { AdminButton } from "@rawa/ui/AdminButton"
import { ApiResponseError } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import { discardFormModule } from "../api"
import { ConfirmDialog } from "./ConfirmDialog"
import { PublishModuleDialog } from "./PublishModuleDialog"
import styles from "./ModuleCanvasActions.module.css"

/**
 * Throws away the module's saved changes, back to the version published
 * (ADR-040), once confirmed. The confirmation stays open until the discard
 * lands, and says why when it does not; the server's answer, handed to
 * `onDiscarded`, replaces the canvas — anything unsaved with it, as the
 * confirmation says.
 */
export function DiscardDraftButton({
  moduleId,
  revision,
  publishedVersion,
  onDiscarded,
}: {
  moduleId: string
  revision: number
  publishedVersion: number
  onDiscarded: (module: FormModuleDetail) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const discard = useMutation({
    mutationFn: () => discardFormModule(moduleId, { revision }),
    onError: (err) => trackError(err, { source: "discardFormModule" }),
    onSuccess: (reverted) => {
      setConfirming(false)
      onDiscarded(reverted)
    },
  })

  return (
    <>
      <AdminButton variant="outline" onClick={() => setConfirming(true)}>
        Discard draft
      </AdminButton>
      <ConfirmDialog
        open={confirming}
        title="Discard draft?"
        confirmLabel="Discard"
        pending={discard.isPending}
        error={discardFailure(discard.error)}
        onCancel={() => {
          setConfirming(false)
          discard.reset()
        }}
        onConfirm={() => discard.mutate()}
      >
        The module goes back to version {publishedVersion} as published. Its
        saved changes, and anything not yet saved, will be lost.
      </ConfirmDialog>
    </>
  )
}

const discardFailure = (error: Error | null): string | undefined => {
  if (!error) return undefined
  return error instanceof ApiResponseError &&
    error.code === FormModuleErrorCode.FORM_MODULE_CHANGED
    ? "Someone has changed this module since you opened it. Reload the page to see their changes."
    : "Could not discard the draft. Please try again."
}

/**
 * Publishes the module's saved changes after the publish dialog has named
 * what they reach (ADR-040). Held, with the reason read to a screen reader,
 * while something is still unsaved — a publish takes what is saved, so it
 * would be left out — or nothing saved is unpublished.
 */
export function PublishButton({
  moduleId,
  moduleName,
  revision,
  change,
  firstPublish,
  unsaved,
  onPublished,
}: {
  moduleId: string
  moduleName: string
  revision: number
  change: "fields" | "wording" | null
  firstPublish: boolean
  unsaved: boolean
  onPublished: (module: FormModuleDetail) => void
}) {
  const [publishing, setPublishing] = useState(false)
  const held = unsaved
    ? "Save the draft before publishing it."
    : change
      ? null
      : "There are no changes to publish."

  return (
    <>
      {held && (
        <p id="publish-held" className={styles.srOnly}>
          {held}
        </p>
      )}
      <AdminButton
        onClick={() => !held && setPublishing(true)}
        ariaDisabled={held !== null}
        describedBy={held ? "publish-held" : undefined}
      >
        Publish
      </AdminButton>
      {publishing && change && (
        <PublishModuleDialog
          open
          moduleId={moduleId}
          moduleName={moduleName}
          revision={revision}
          change={change}
          firstPublish={firstPublish}
          onClose={() => setPublishing(false)}
          onPublished={(published) => {
            setPublishing(false)
            onPublished(published)
          }}
        />
      )}
    </>
  )
}
